/**
 * Boot Slice - Consolidated boot status state machine
 *
 * Provides a single source of truth for app boot status, primarily for:
 * - Sentry error tagging (correlate errors with boot phase)
 * - Debugging (clear snapshot of what's ready/blocked)
 * - Deterministic gating in route-specific bootstraps
 *
 * Status values:
 * - "cold": Initial state before any initialization
 * - "booting": Initialization in progress
 * - "ready": All required systems initialized for current route
 * - "blocked": Unrecoverable boot failure (not just "unauthenticated")
 *
 * This slice does NOT replace existing providers - it consolidates their
 * state for observability. Route-specific bootstrap components dispatch
 * to this slice as they progress through initialization.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getBootId, getBootTimestamp } from '../../utils/bootSession';

export type BootStatus = 'cold' | 'booting' | 'ready' | 'blocked';

/**
 * Granular sub-states for debugging what's ready/pending
 */
export interface BootSubStates {
  csrfReady: boolean;
  authResolved: boolean; // Auth check complete (may be authenticated or not)
  profileLoaded: boolean;
  orgLoaded: boolean;
}

export interface BootState {
  status: BootStatus;
  reason?: string; // Human-readable reason for blocked state
  bootId: string; // From bootSession.ts - correlates all requests in this session
  startedAt?: number; // When boot started
  readyAt?: number; // When boot completed
  subStates: BootSubStates;
  /** Route context for debugging (e.g., "dashboard", "auth") */
  routeContext?: string;
  /** Number of boot attempts (increments on retry) */
  bootAttempt: number;
  /**
   * True when initial conversations load timed out before data arrived.
   * UI should show "Unable to load" instead of "No conversations" to avoid
   * misleading users into thinking they have no conversations.
   */
  conversationsLoadDegraded: boolean;
}

// Use a stable initial value for SSR hydration - will be updated on client
const initialState: BootState = {
  status: 'cold',
  bootId: 'pending', // Stable for hydration - set to real bootId after mount
  subStates: {
    csrfReady: false,
    authResolved: false,
    profileLoaded: false,
    orgLoaded: false,
  },
  bootAttempt: 0,
  conversationsLoadDegraded: false,
};

const bootSlice = createSlice({
  name: 'boot',
  initialState,
  reducers: {
    /**
     * Start the boot sequence for a route context
     */
    bootStart(state, action: PayloadAction<{ routeContext: string }>) {
      // Only transition from cold or blocked (retry)
      if (state.status === 'cold' || state.status === 'blocked') {
        state.status = 'booting';
        // Only set bootId/startedAt on client - SSR should not dispatch bootStart
        if (typeof window !== 'undefined') {
          state.bootId = getBootId();
          state.startedAt = getBootTimestamp();
        }
        state.readyAt = undefined;
        state.reason = undefined;
        state.routeContext = action.payload.routeContext;
        state.bootAttempt += 1;
      }
    },

    /**
     * Update individual sub-states as they become ready
     */
    bootSubStateUpdate(
      state,
      action: PayloadAction<Partial<BootSubStates>>
    ) {
      state.subStates = { ...state.subStates, ...action.payload };
    },

    /**
     * Mark boot as ready (all required systems initialized)
     */
    bootReady(state) {
      if (state.status === 'booting') {
        state.status = 'ready';
        state.readyAt = Date.now();
        state.reason = undefined;
      }
    },

    /**
     * Mark boot as blocked due to unrecoverable error
     * This is NOT for "unauthenticated" - that's a redirect, not a block
     */
    bootBlocked(state, action: PayloadAction<{ reason: string }>) {
      state.status = 'blocked';
      state.reason = action.payload.reason;
    },

    /**
     * Reset boot state (e.g., on logout or navigation to non-gated route)
     */
    bootReset(state) {
      state.status = 'cold';
      state.reason = undefined;
      state.startedAt = undefined;
      state.readyAt = undefined;
      state.routeContext = undefined;
      state.subStates = {
        csrfReady: false,
        authResolved: false,
        profileLoaded: false,
        orgLoaded: false,
      };
      state.conversationsLoadDegraded = false;
    },

    /**
     * Mark conversations load as degraded (timed out before data arrived).
     * Called when the UI timeout fires and we have no conversation data yet.
     * This ensures the UI shows "Unable to load" instead of "No conversations".
     */
    setConversationsLoadDegraded(state, action: PayloadAction<boolean>) {
      state.conversationsLoadDegraded = action.payload;
    },
  },
});

export const {
  bootStart,
  bootSubStateUpdate,
  bootReady,
  bootBlocked,
  bootReset,
  setConversationsLoadDegraded,
} = bootSlice.actions;

export default bootSlice.reducer;

// Selectors
export const selectBootStatus = (state: { boot: BootState }) => state.boot.status;
export const selectBootSubStates = (state: { boot: BootState }) => state.boot.subStates;
export const selectBootId = (state: { boot: BootState }) => state.boot.bootId;
export const selectIsBootReady = (state: { boot: BootState }) => state.boot.status === 'ready';
export const selectIsBootBlocked = (state: { boot: BootState }) => state.boot.status === 'blocked';
export const selectBootReason = (state: { boot: BootState }) => state.boot.reason;
export const selectConversationsLoadDegraded = (state: { boot: BootState }) => state.boot.conversationsLoadDegraded;

/**
 * Get boot context for Sentry tagging
 */
export const selectBootSentryContext = (state: { boot: BootState }) => ({
  boot_id: state.boot.bootId,
  boot_status: state.boot.status,
  boot_route: state.boot.routeContext,
  boot_attempt: state.boot.bootAttempt,
  boot_duration_ms: state.boot.readyAt && state.boot.startedAt
    ? state.boot.readyAt - state.boot.startedAt
    : undefined,
  csrf_ready: state.boot.subStates.csrfReady,
  auth_resolved: state.boot.subStates.authResolved,
  profile_loaded: state.boot.subStates.profileLoaded,
  org_loaded: state.boot.subStates.orgLoaded,
  conversations_load_degraded: state.boot.conversationsLoadDegraded,
});
